/**
 * File: src/services/admin-config-service.js
 * Mô tả: Dịch vụ quản trị cấu hình (Admin Config Service), hỗ trợ gửi API /api/admin/save-config kèm Auth Header tới Cloudflare Pages Function.
 * Liên quan: functions/api/admin/save-config.js, src/services/admin-auth-service.js
 */

import { defaultAntClashConfig } from '../data/ant-clash-config-default.js';
import { validateAntClashConfig } from '../validation/ant-clash-config-schema.js';
import { createConfigEnvelope, encodeConfigPayload } from './config-envelope-service.js';
import { getAdminSession } from './admin-auth-service.js';

const STORAGE_KEY_PREFIX = 'snt_game_config_';

/**
 * So sánh hai chuỗi version dạng semver (trả về true nếu vA > vB)
 * @param {string} vA 
 * @param {string} vB 
 * @returns {boolean}
 */
export const isVersionNewer = (vA, vB) => {
  if (!vA) return false;
  if (!vB) return true;
  const pA = String(vA).replace(/^v/, '').split('.').map(Number);
  const pB = String(vB).replace(/^v/, '').split('.').map(Number);
  for (let i = 0; i < Math.max(pA.length, pB.length); i++) {
    const numA = pA[i] || 0;
    const numB = pB[i] || 0;
    if (numA > numB) return true;
    if (numA < numB) return false;
  }
  return false;
};

/**
 * Lấy danh sách toàn bộ lịch sử Revisions
 * @param {string} [gameId='ant-clash']
 * @returns {Array}
 */
export const getRevisionHistory = (gameId = 'ant-clash') => {
  const initialRevision = {
    revisionId: 1,
    version: '1.0.338',
    publishedAt: new Date().toISOString(),
    author: 'System Admin',
    note: 'Phiên bản cấu hình cân bằng tối ưu v1.0.338 cho Ant Clash',
    configData: defaultAntClashConfig,
    envelope: createConfigEnvelope(gameId, '1.0.338', defaultAntClashConfig),
  };

  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${gameId}_revisions`);
    if (raw) {
      const history = JSON.parse(raw);
      if (Array.isArray(history) && history.length > 0) {
        const lastRev = history[history.length - 1];
        // Nếu phiên bản lưu trong localStorage cũ hơn v1.0.338 từ code, tự động nâng cấp
        if (lastRev && isVersionNewer('1.0.338', lastRev.version)) {
          const newRevId = (lastRev.revisionId || 1) + 1;
          const upgradedRev = {
            ...initialRevision,
            revisionId: newRevId,
            note: 'Tự động nâng cấp lên cấu hình v1.0.338 từ mã nguồn',
          };
          history.push(upgradedRev);
          saveHistoryToStorage(gameId, history);
        }
        return history;
      }
    }
  } catch (e) {
    console.error('Lỗi khi đọc lịch sử revision:', e);
  }

  const history = [initialRevision];
  saveHistoryToStorage(gameId, history);
  return history;
};

/**
 * Khôi phục cấu hình về bản v1.0.324 mặc định
 * @param {string} [gameId='ant-clash']
 * @returns {Object}
 */
export const resetToDefaultRevision = (gameId = 'ant-clash') => {
  const defaultRev = {
    revisionId: 1,
    version: '1.0.338',
    publishedAt: new Date().toISOString(),
    author: 'System Admin',
    note: 'Phiên bản cấu hình cân bằng tối ưu v1.0.338 cho Ant Clash',
    configData: defaultAntClashConfig,
    envelope: createConfigEnvelope(gameId, '1.0.338', defaultAntClashConfig),
  };
  saveHistoryToStorage(gameId, [defaultRev]);
  return defaultRev;
};

/**
 * Lấy bản cấu hình active mới nhất
 * @param {string} [gameId='ant-clash']
 * @returns {Object}
 */
export const getActiveConfigRevision = (gameId = 'ant-clash') => {
  const history = getRevisionHistory(gameId);
  return history[history.length - 1];
};

/**
 * Lưu phiên bản cấu hình mới (Đồng bộ qua Cloudflare Pages Function API và LocalStorage)
 * @param {string} gameId 
 * @param {Object} newConfigData 
 * @param {string} author 
 * @param {string} note 
 * @returns {Promise<{ success: boolean, message: string, revision?: Object, errors?: string[] }>}
 */
export const saveNewConfigRevision = async (gameId, newConfigData, author = 'Admin', note = 'Cập nhật cấu hình') => {
  const validation = validateAntClashConfig(newConfigData);
  if (!validation.valid) {
    return { success: false, message: 'Dữ liệu cấu hình không hợp lệ.', errors: validation.errors };
  }

  const history = getRevisionHistory(gameId);
  const lastRev = history[history.length - 1];
  const nextRevId = lastRev ? lastRev.revisionId + 1 : 1;
  let nextVersion = '1.0.339';
  if (lastRev && lastRev.version) {
    const parts = lastRev.version.split('.');
    if (parts.length === 3 && !isNaN(Number(parts[2]))) {
      nextVersion = `${parts[0]}.${parts[1]}.${Number(parts[2]) + 1}`;
    } else {
      nextVersion = `1.0.${nextRevId}`;
    }
  }
  let serverSynced = false;

  const session = getAdminSession();
  const authToken = session?.token || '';

  try {
    const res = await fetch('/api/admin/save-config', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ gameId, configData: newConfigData, version: nextVersion, author, note }),
    });
    if (res.ok) {
      const apiResult = await res.json();
      if (apiResult.success) {
        serverSynced = true;
        if (apiResult.version) nextVersion = apiResult.version;
      }
    } else if (res.status === 401) {
      return { success: false, message: '⛔ Bạn chưa đăng nhập Admin hoặc phiên đã hết hạn. Vui lòng đăng nhập lại!' };
    }
  } catch (e) {
    console.warn('Chưa kết nối Cloudflare Pages Function API (Đang chạy môi trường Local Vite dev):', e);
  }

  const newRevision = {
    revisionId: nextRevId,
    version: nextVersion,
    publishedAt: new Date().toISOString(),
    author,
    note,
    configData: newConfigData,
    envelope: createConfigEnvelope(gameId, nextVersion, newConfigData),
  };

  history.push(newRevision);
  saveHistoryToStorage(gameId, history);

  const statusMsg = serverSynced
    ? `✅ Đã lưu phiên bản v${nextVersion} lên Cloudflare KV & xuất bản file tĩnh (/config/${gameId}/game-config.bin) thành công!`
    : `✅ Đã lưu phiên bản v${nextVersion} (Local). Gửi API Pages Function thất bại (Đang chạy Local Vite).`;

  return { success: true, message: statusMsg, revision: newRevision };
};

/**
 * Khôi phục (Rollback) về phiên bản cũ
 * @param {string} gameId 
 * @param {number} targetRevisionId 
 * @param {string} author 
 * @returns {Promise<{ success: boolean, message: string }>}
 */
export const rollbackToRevision = async (gameId, targetRevisionId, author = 'Admin') => {
  const history = getRevisionHistory(gameId);
  const target = history.find((r) => r.revisionId === targetRevisionId);

  if (!target) {
    return { success: false, message: `Không tìm thấy Revision #${targetRevisionId}.` };
  }

  return saveNewConfigRevision(gameId, target.configData, author, `Rollback khôi phục dữ liệu từ Revision #${targetRevisionId}`);
};

/**
 * Xuất file tĩnh (download info.json và game-config.bin)
 * @param {string} gameId 
 */
export const exportStaticConfigFile = (gameId = 'ant-clash') => {
  const activeRev = getActiveConfigRevision(gameId);
  const encodedBin = encodeConfigPayload(activeRev.configData);

  const infoObj = {
    gameId,
    version: activeRev.version,
    updatedAt: activeRev.publishedAt,
    configUrl: `/config/${gameId}/game-config.bin`,
  };

  downloadTextFile(`game-config.bin`, encodedBin);
  setTimeout(() => {
    downloadTextFile(`info.json`, JSON.stringify(infoObj, null, 2));
  }, 300);
};

const downloadTextFile = (filename, content) => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const saveHistoryToStorage = (gameId, history) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${gameId}_revisions`, JSON.stringify(history));
  } catch (e) {
    console.error('Lỗi khi ghi dữ liệu vào localStorage:', e);
  }
};
